import { type FormEvent, useEffect, useRef, useState } from 'react';

interface Props {
  onSend: (body: string) => Promise<void> | void;
  disabled?: boolean;
  placeholder?: string;
}

const MAX_LEN = 2000;

/**
 * Multiline-aware composer. Enter sends; Shift+Enter inserts a newline.
 * Auto-grows up to ~6 lines, scrolls after that.
 */
export function Composer({ onSend, disabled, placeholder = 'Describe the issue…' }: Props) {
  const [body, setBody] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
  }, []);

  function handleInput(value: string) {
    setBody(value.slice(0, MAX_LEN));
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
    }
  }

  async function submit(ev: FormEvent | KeyboardEvent) {
    ev.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || disabled) return;
    setBody('');
    await onSend(trimmed);
    // Reset height after send.
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'flex-end',
        padding: 'var(--space-4) var(--space-6)',
        borderTop: '1px solid var(--rule)',
        background: 'var(--paper)',
      }}
    >
      <textarea
        ref={textareaRef}
        value={body}
        rows={1}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => handleInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            void submit(e.nativeEvent);
          }
        }}
        style={{
          flex: 1,
          padding: '10px 14px',
          border: '1px solid var(--rule)',
          borderRadius: 'var(--radius-md)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body)',
          lineHeight: 'var(--lh-body)',
          color: 'var(--ink)',
          background: 'var(--paper)',
          minHeight: 44,
          maxHeight: 160,
          overflow: 'auto',
        }}
      />
      <button
        type="submit"
        className="btn-accent"
        disabled={disabled || body.trim().length === 0}
        style={{ flexShrink: 0 }}
      >
        Send
      </button>
    </form>
  );
}
